/**
 * BottomNav Component - Main navigation
 */

import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Radio, Compass, User, LayoutDashboard, Play, Clapperboard, Store, CalendarDays, Camera } from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { cn } from '../utils/helpers';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const BottomNav = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated } = useAuth();
  const [eventPhotos, setEventPhotos] = useState(0);

  // Lightweight "new pics" discovery badge on the Events tab
  useEffect(() => {
    let active = true;
    axios.get(`${API_URL}/api/events/gallery/feed?limit=1`)
      .then(res => { if (active) setEventPhotos(res.data?.count || (res.data?.photos || []).length || 0); })
      .catch(() => {});
    return () => { active = false; };
  }, [location.pathname]);

  const navItems = [
    { path: '/', icon: Radio, label: 'Live' },
    { path: '/clips', icon: Play, label: 'Clips' },
    { path: '/discover', icon: Compass, label: 'Discover' },
    { path: '/events', icon: CalendarDays, label: 'Events' },
    { path: '/marketplace', icon: Store, label: 'Shop' },
    { path: '/stories', icon: Clapperboard, label: 'Stories', requiresAuth: true },
    { path: '/profile', icon: User, label: 'Profile', requiresAuth: true },
    { path: '/dashboard', icon: LayoutDashboard, label: 'Dashboard', requiresAuth: true },
  ];

  const visibleItems = navItems.filter(item => !item.requiresAuth || isAuthenticated);

  return (
    <nav className="fixed bottom-0 left-0 right-0 z-50 bg-[#0B0A10]/90 backdrop-blur-xl border-t border-white/10" data-testid="bottom-nav">
      <div className="flex items-center justify-around h-16 max-w-lg mx-auto px-0.5">
        {visibleItems.map((item) => {
          const isActive = location.pathname === item.path;
          const Icon = item.icon;
          const showPicsBadge = item.path === '/events' && !isActive && eventPhotos > 0;
          
          return (
            <button
              key={item.path}
              onClick={() => navigate(item.path)}
              className="relative flex flex-1 min-w-0 flex-col items-center gap-0.5 py-2 px-0.5"
              data-testid={`nav-${item.label.toLowerCase()}`}
            >
              {isActive && (
                <motion.div
                  layoutId="activeTab"
                  className="absolute inset-x-0.5 inset-y-0 bg-white/10 rounded-xl"
                  transition={{ type: 'spring', stiffness: 500, damping: 30 }}
                />
              )}
              <div className="relative z-10">
                <Icon className={cn(
                  "w-5 h-5 sm:w-6 sm:h-6 transition-colors",
                  isActive ? "text-[#00F0FF]" : "text-[#A1A1AA]"
                )} />
                {showPicsBadge && (
                  <motion.span
                    initial={{ scale: 0 }}
                    animate={{ scale: [1, 1.15, 1] }}
                    transition={{ duration: 1.6, repeat: Infinity }}
                    className="absolute -top-1.5 -right-2 flex items-center gap-0.5 px-1.5 py-0.5 rounded-full bg-[#FF007F] shadow-[0_0_8px_#FF007F]"
                    data-testid="events-new-pics-badge"
                  >
                    <Camera className="w-2.5 h-2.5 text-white" />
                    <span className="text-[8px] font-bold text-white leading-none">Pics</span>
                  </motion.span>
                )}
              </div>
              <span className={cn(
                "text-[10px] sm:text-xs relative z-10 transition-colors leading-none",
                isActive ? "text-white" : "text-[#A1A1AA]"
              )}>
                {item.label}
              </span>
            </button>
          );
        })}
      </div>
    </nav>
  );
};

export default BottomNav;
